import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <SiteLayout>
      <SEO
        title="Política de Privacidad · Mujeres Motor"
        description="Cómo Mujeres Motor recolecta, usa y protege tus datos. Cookies, GA4, formularios, chat editorial y derechos del usuario."
        url="https://mujeres-motor.com/privacy"
      />
      <section className="bg-cream pt-28 pb-24">
        <Container>
          <div className="max-w-3xl mx-auto" data-testid="privacy-page">
            <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-3">
              Política
            </p>
            <h1 className="font-serif text-4xl md:text-5xl font-light text-charcoal leading-tight">
              Política de Privacidad
            </h1>
            <p className="text-charcoal/55 mt-3 text-sm">
              Última actualización: 26 de abril, 2026
            </p>

            <div className="prose prose-editorial mt-12 space-y-8 text-charcoal/85 leading-relaxed">
              <p>
                En <strong>Mujeres Motor</strong> tratamos tus datos con el mismo cuidado con que
                producimos las historias que ves en nuestra plataforma. Este documento explica
                qué recopilamos, por qué, y cómo puedes ejercer tus derechos.
              </p>

              <Section title="1. Quién es responsable">
                <p>
                  Mujeres Motor es una plataforma editorial premium operada por el equipo
                  liderado por la directora editorial Mariola Quesada. Para cualquier consulta
                  sobre privacidad, escríbenos a{" "}
                  <a
                    href="mailto:mariola@mujeres-motor.com"
                    className="text-terracotta underline underline-offset-2 hover:text-charcoal"
                  >
                    mariola@mujeres-motor.com
                  </a>
                  .
                </p>
              </Section>

              <Section title="2. Datos que recolectamos">
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Formularios públicos</strong> (Newsletter, Comunidad, Postula una
                    historia, Para marcas, RSVP a eventos): nombre, correo, ciudad, industria,
                    empresa o medio según aplique. Lo que tú ingreses, nada más.
                  </li>
                  <li>
                    <strong>Conversaciones del chat editorial</strong>: el contenido de tus
                    mensajes y, si decides dejarnos contacto, tu nombre, correo y empresa.
                    Conservamos los transcripts hasta 90 días.
                  </li>
                  <li>
                    <strong>Datos de navegación</strong>: páginas visitadas, parámetros UTM
                    (utm_source, medium, campaign), referrer y timestamps. Esta información
                    se usa para mejorar el contenido y entender qué historias resuenan.
                  </li>
                  <li>
                    <strong>Datos técnicos</strong>: dirección IP (anonimizada para analítica),
                    user agent, tipo de dispositivo. No combinamos esto con datos personales
                    salvo que tú lo elijas (por ejemplo al postular).
                  </li>
                </ul>
              </Section>

              <Section title="3. Cookies y herramientas de analítica">
                <p>
                  Usamos <strong>Google Analytics 4</strong> y <strong>Google Tag Manager</strong>{" "}
                  para entender el rendimiento del sitio. Activamos el modo "Consent Mode v2"
                  de Google: hasta que aceptas el banner, las cookies de analítica permanecen{" "}
                  <em>denegadas</em>. Si rechazas, no se envían eventos identificables a Google.
                </p>
                <p>
                  Tipos de cookies que usamos:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li><strong>Funcionales</strong> (siempre activas): mantienen tu sesión, idioma y preferencias.</li>
                  <li><strong>Analíticas</strong> (opt-in): GA4 para entender uso agregado.</li>
                  <li><strong>Publicitarias</strong> (opt-in): hoy no las usamos, pero la infraestructura de Consent Mode las contempla por si en el futuro lanzamos remarketing.</li>
                </ul>
                <p>
                  Puedes cambiar tu decisión en cualquier momento borrando el localStorage
                  desde tu navegador o escribiéndonos.
                </p>
              </Section>

              <Section title="4. Cómo usamos tus datos">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Para responder tus mensajes y postulaciones.</li>
                  <li>Para enviarte el newsletter editorial si te suscribes (nunca sin tu consentimiento).</li>
                  <li>Para coordinar eventos de comunidad si decides participar.</li>
                  <li>Para mejorar el contenido editorial y la experiencia del sitio (analítica agregada).</li>
                  <li>Para cumplir obligaciones legales si aplica.</li>
                </ul>
                <p className="mt-2">
                  No vendemos tus datos. No los compartimos con terceros para fines publicitarios.
                </p>
              </Section>

              <Section title="5. Compartición con terceros">
                <p>
                  Trabajamos con un número reducido de proveedores que nos ayudan a operar la
                  plataforma:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li><strong>Resend</strong>: envío de correos transaccionales y newsletter.</li>
                  <li><strong>Cloudflare R2</strong>: almacenamiento de imágenes y assets.</li>
                  <li><strong>Google Analytics + GTM</strong>: analítica anónima.</li>
                  <li><strong>Behold.so</strong>: integración del feed público de Instagram.</li>
                  <li><strong>OpenAI</strong> (vía Emergent): genera respuestas del chat editorial. No envía datos personales identificables a OpenAI.</li>
                </ul>
              </Section>

              <Section title="6. Tus derechos">
                <p>
                  Tienes derecho a acceder, corregir, eliminar o portar tus datos. También
                  puedes revocar tu consentimiento de analítica o solicitar el borrado de tus
                  conversaciones del chat. Para ejercer cualquiera, escríbenos a{" "}
                  <a
                    href="mailto:mariola@mujeres-motor.com"
                    className="text-terracotta underline underline-offset-2"
                  >
                    mariola@mujeres-motor.com
                  </a>{" "}
                  con el asunto "Privacidad — [tu solicitud]". Te respondemos en un máximo de
                  10 días hábiles.
                </p>
              </Section>

              <Section title="7. Conservación de datos">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Suscripciones a newsletter: hasta que des de baja.</li>
                  <li>Postulaciones y formularios: 24 meses para fines editoriales.</li>
                  <li>Transcripts del chat: 90 días automáticos, después se anonimizan o eliminan.</li>
                  <li>Analítica agregada: hasta 26 meses (configuración estándar de GA4).</li>
                </ul>
              </Section>

              <Section title="8. Cambios a esta política">
                <p>
                  Si actualizamos materialmente esta política, lo verás reflejado en la fecha
                  de "última actualización" arriba y, si te has suscrito al newsletter, te
                  enviaremos un aviso editorial.
                </p>
              </Section>

              <p className="text-charcoal/55 text-sm pt-6 border-t border-charcoal/10">
                ¿Dudas? Escríbenos a{" "}
                <a
                  href="mailto:mariola@mujeres-motor.com"
                  className="text-terracotta underline underline-offset-2"
                >
                  mariola@mujeres-motor.com
                </a>
                . O regresa a la{" "}
                <Link to="/" className="text-terracotta underline underline-offset-2">
                  página principal
                </Link>
                .
              </p>
            </div>
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-serif text-2xl text-charcoal font-light mb-3">{title}</h2>
      <div className="space-y-3 text-base">{children}</div>
    </div>
  );
}
